import { useMemo } from "react";
import { create, useStore } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { generateGradingTable } from "./grades";
import { GradingFormSchema } from "./gradingSchema";

// Module-level timeout reference for highlight clearing
let highlightTimeoutId: NodeJS.Timeout | null = null;

interface GradeState {
  data: GradingFormSchema;
  store: (data: GradingFormSchema) => void;
  _hasHydrated: boolean;
  setHasHydrated: (hasHydrated: boolean) => void;
  highlightedScore: number | null;
  setHighlightedScore: (score: number | null) => void;
  highlightTableRow: (score: number) => void;
}

export const useGrade = create<GradeState>()(
  devtools(
    persist(
      (set) => ({
        _hasHydrated: false,
        setHasHydrated: (hasHydrated: boolean) =>
          set({ _hasHydrated: hasHydrated }),
        data: {
          passingGrade: "5.5",
          maxScore: "30",
          term: "100",
          pointsType: "full",
          linearPercentage: "50",
        },
        store: (data) => {
          set({ data: data });
        },
        highlightedScore: null,
        setHighlightedScore: (score: number | null) =>
          set({ highlightedScore: score }),
        highlightTableRow: (score: number) => {
          // Clear any existing timeout
          if (highlightTimeoutId) {
            clearTimeout(highlightTimeoutId);
          }

          set({ highlightedScore: score });

          // Scroll to row
          const row = document.querySelector(`[data-score="${score}"]`);
          row?.scrollIntoView({ behavior: "smooth", block: "center" });

          // Clear highlight after animation
          highlightTimeoutId = setTimeout(() => {
            set({ highlightedScore: null });
            highlightTimeoutId = null;
          }, 2000);
        },
      }),
      {
        name: "grading",
        onRehydrateStorage: (state) => {
          return () => state.setHasHydrated(true);
        },
        partialize: (state) => ({ data: state.data }),
      },
    ),
  ),
);

export const useGradingTable = () => {
  const { data } = useStore(useGrade, (store) => store);

  const possibleScores = useMemo(() => {
    const maxScore = Number(data.maxScore);
    const term = Number(data.term);
    const linearPercentage = Number(data.linearPercentage);
    if (maxScore <= 0) {
      return [];
    } else {
      return generateGradingTable(
        maxScore,
        term,
        linearPercentage,
        data.pointsType,
        Number(data.passingGrade),
      );
    }
  }, [data]);

  return {
    ...data,
    maxScore: Math.min(Number(data.maxScore), 300),
    possibleScores,
  };
};
